import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Database, Users, Activity, Wrench, Boxes } from "lucide-react";

const FEEDS = [
  { key: "market", label: "Market share (Vahan)", icon: Database },
  { key: "manpower", label: "HR manpower", icon: Users },
  { key: "dpc", label: "Daily performance (LETBR)", icon: Activity },
  { key: "service", label: "After-Sales DMS", icon: Wrench },
  { key: "stock", label: "Stock dump (DMS)", icon: Boxes },
];

export const fmtIST = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false });
};

const fmtDate = (s) => (s ? new Date(`${s}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : null);

export const ageTone = (ageDays) => {
  if (ageDays == null) return "bg-muted-foreground/40";
  if (ageDays > 3) return "bg-red-500";
  if (ageDays > 1) return "bg-amber-500";
  return "bg-emerald-500";
};

export function useFreshness() {
  const [data, setData] = useState(null);
  useEffect(() => {
    const load = () => api.get("/freshness").then(({ data }) => setData(data)).catch(() => {});
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, []);
  return data;
}

export function FreshnessFooter({ variant = "sidebar", testId = "freshness-footer" }) {
  const data = useFreshness();
  if (!data) return null;
  if (variant === "inline") {
    return (
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground" data-testid="freshness-inline">
        {FEEDS.map((f) => {
          const it = data[f.key];
          if (!it) return null;
          return (
            <span key={f.key} className="inline-flex items-center gap-1.5" data-testid={`freshness-inline-${f.key}`}>
              <span className={`h-2 w-2 rounded-full ${ageTone(it.age_days)}`} />
              {f.label}: <b className="text-foreground">{it.uploaded_at ? fmtIST(it.uploaded_at) : "no upload yet"}</b>
              {it.data_date && <span>(data {fmtDate(it.data_date)})</span>}
            </span>
          );
        })}
      </div>
    );
  }
  return (
    <div className="px-4 py-3 border-t space-y-2" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Data as of</div>
      {FEEDS.map((f) => {
        const it = data[f.key];
        const Icon = f.icon;
        if (!it) return null;
        return (
          <div key={f.key} className="flex items-start gap-2" data-testid={`freshness-${f.key}`} title={it.file ? `${it.file}${it.by ? ` · by ${it.by}` : ""}` : "No upload yet"}>
            <span className={`mt-1 h-2 w-2 rounded-full shrink-0 ${ageTone(it.age_days)}`} data-testid={`freshness-dot-${f.key}`} />
            <Icon size={12} className="mt-0.5 shrink-0 text-muted-foreground" />
            <div className="min-w-0 leading-tight">
              <div className="text-[11px] font-medium truncate">{f.label}</div>
              <div className="text-[10px] text-muted-foreground" data-testid={`freshness-when-${f.key}`}>{it.uploaded_at ? `Uploaded ${fmtIST(it.uploaded_at)}` : "No upload yet"}</div>
              {it.data_date && <div className="text-[10px] text-muted-foreground" data-testid={`freshness-date-${f.key}`}>Data up to {fmtDate(it.data_date)}</div>}
              {it.by && <div className="text-[10px] text-muted-foreground/80 truncate">by {it.by}</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
